/**
 * MonitoredProductsTable Component
 *
 * Displays tracked products in an IndexTable with status badges.
 * Clicking a product opens the settings modal for threshold configuration.
 */

import {
  IndexTable,
  Badge,
  Text,
  InlineStack,
  Box,
  Icon,
} from '@shopify/polaris';
import { ChevronLeftIcon, ChevronRightIcon } from '@shopify/polaris-icons';
import {
  AlertTriangleIcon,
  AlertCircleIcon,
  InfoIcon,
  CheckCircleIcon,
} from '@shopify/polaris-icons';
import type { TrackedProduct, OnboardingData } from '../../types/dashboard';
import { ProductCell } from './ProductCell';
import { BadgeWithTooltip } from '../ui/BadgeWithTooltip';
import { DataPill } from '../ui/DataPill';

/**
 * Props for MonitoredProductsTable component
 */
export interface MonitoredProductsTableProps {
  products: TrackedProduct[];
  onboardingData: OnboardingData | null;
  shopDomain: string;
  /** Called when a product row is clicked - opens settings modal */
  onProductClick: (product: TrackedProduct) => void;
  /** Whether the shop has test data (used for test badge display) */
  hasTestData?: boolean;
  // Pagination
  currentPage: number;
  totalPages: number;
  itemsPerPage: number;
  onPageChange: (page: number) => void;
}

/** Tooltip content for status badges */
const STATUS_TOOLTIPS = {
  critical: 'Stock levels are critically low. Immediate action recommended.',
  warning: 'Stock is running low. Consider reordering soon.',
  pending: 'Waiting for inventory data from Shopify.',
  healthy: 'Stock levels are healthy based on current velocity.',
};

/**
 * Check if a product title indicates test data
 */
function isTestProduct(title: string): boolean {
  return /\(Test\s*\d*\)/i.test(title);
}

/**
 * Get status badge for a product based on alert severities
 */
function getStatusBadge(product: TrackedProduct) {
  const alertSeverities = product.alert_severities ? product.alert_severities.split(',') : [];
  const hasCritical = alertSeverities.includes('critical');
  const hasWarning = alertSeverities.includes('warning');
  const hasData = product.variant_count > 0 || product.total_inventory > 0;

  if (!hasData) {
    return (
      <BadgeWithTooltip
        badge={<Badge tone="info" icon={InfoIcon}>Pending data</Badge>}
        tooltipContent={STATUS_TOOLTIPS.pending}
      />
    );
  } else if (hasCritical) {
    return (
      <BadgeWithTooltip
        badge={<Badge tone="critical" icon={AlertTriangleIcon}>Critical</Badge>}
        tooltipContent={STATUS_TOOLTIPS.critical}
      />
    );
  } else if (hasWarning) {
    return (
      <BadgeWithTooltip
        badge={<Badge tone="warning" icon={AlertCircleIcon}>Warning</Badge>}
        tooltipContent={STATUS_TOOLTIPS.warning}
      />
    );
  } else {
    return (
      <BadgeWithTooltip
        badge={<Badge tone="success" icon={CheckCircleIcon}>Healthy</Badge>}
        tooltipContent={STATUS_TOOLTIPS.healthy}
      />
    );
  }
}

/**
 * Monitored Products IndexTable component
 */
export function MonitoredProductsTable({
  products,
  onboardingData,
  shopDomain,
  onProductClick,
  hasTestData = false,
  currentPage,
  totalPages,
  itemsPerPage,
  onPageChange,
}: MonitoredProductsTableProps) {
  // Calculate paginated products
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const paginatedProducts = products.slice(startIndex, endIndex);

  // Format threshold display
  const thresholdDisplay = onboardingData?.currentSettings
    ? `${onboardingData.currentSettings.urgent_threshold_days}d / ${onboardingData.currentSettings.warning_threshold_days}d`
    : '—';

  return (
    <>
      <div className="animated-list">
        <IndexTable
          resourceName={{ singular: 'product', plural: 'products' }}
          itemCount={paginatedProducts.length}
          headings={[
            { title: 'Product' },
            { title: 'Variants', alignment: 'end' },
            { title: 'Stock', alignment: 'end' },
            { title: 'Thresholds', alignment: 'center' },
            { title: 'Status', alignment: 'center' },
          ]}
          selectable={false}
        >
          {paginatedProducts.map((product, index) => (
            <IndexTable.Row
              id={product.product_id}
              key={product.product_id}
              position={index}
              onClick={() => {
                if (shopDomain) {
                  onProductClick(product);
                }
              }}
            >
              <IndexTable.Cell>
                <ProductCell
                  title={product.product_title}
                  imageUrl={product.image_url}
                  showTestBadge={hasTestData && isTestProduct(product.product_title)}
                />
              </IndexTable.Cell>
              <IndexTable.Cell>
                <InlineStack align="end">
                  <DataPill value={product.variant_count || 0} label="variants" />
                </InlineStack>
              </IndexTable.Cell>
              <IndexTable.Cell>
                <InlineStack align="end">
                  <DataPill value={product.total_inventory || 0} label="units" />
                </InlineStack>
              </IndexTable.Cell>
              <IndexTable.Cell>
                <InlineStack align="center" blockAlign="center">
                  <Text as="span" variant="bodyMd">
                    {thresholdDisplay}
                  </Text>
                </InlineStack>
              </IndexTable.Cell>
              <IndexTable.Cell>
                <InlineStack align="center" blockAlign="center">
                  {getStatusBadge(product)}
                </InlineStack>
              </IndexTable.Cell>
            </IndexTable.Row>
          ))}
        </IndexTable>
      </div>

      {/* Pagination - using native buttons for reliable click handling in embedded apps */}
      {products.length > itemsPerPage && (
        <Box paddingBlockStart="400">
          <InlineStack align="center" gap="200" blockAlign="center">
            <button
              type="button"
              className="pagination-button"
              onClick={() => onPageChange(currentPage - 1)}
              disabled={currentPage <= 1}
              aria-label="Previous page"
            >
              <Icon source={ChevronLeftIcon} />
            </button>
            <Text as="span" variant="bodyMd">
              Page {currentPage} of {totalPages}
            </Text>
            <button
              type="button"
              className="pagination-button"
              onClick={() => onPageChange(currentPage + 1)}
              disabled={currentPage >= totalPages}
              aria-label="Next page"
            >
              <Icon source={ChevronRightIcon} />
            </button>
          </InlineStack>
          <Box paddingBlockStart="200">
            <InlineStack align="center">
              <Text as="p" variant="bodySm" tone="subdued" alignment="center">
                Showing {startIndex + 1}-{Math.min(endIndex, products.length)} of {products.length} products
              </Text>
            </InlineStack>
          </Box>
        </Box>
      )}
    </>
  );
}
